/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: AuditLogGrid.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Data grid específico para Audit Logs con filtros integrados
────────────────────────────────────────────────────────────────*/

import { ScrollText } from 'lucide-react';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import DateRangePicker, { DateRange } from '../../../components/ui/DateRangePicker';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { AuditLogFilterValues } from '../types/auditLog.types';
import { ActionMultiSelect } from './ActionMultiSelect';
import { useUsersList } from '../hooks/useUsersList';

interface AuditLogGridProps {
  data: unknown[];
  columns: Array<{ key: string; label: string; visible: boolean; sortable?: boolean }>;
  loading: boolean;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  searchQuery: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: AuditLogFilterValues;
  onSearch: (query: string) => void;
  onSort: (column: string) => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (limit: number) => void;
  onColumnToggle: (key: string) => void;
  onFilterChange: (filters: AuditLogFilterValues) => void;
}

export function AuditLogGrid({
  data,
  columns,
  loading,
  pagination,
  searchQuery,
  sortBy,
  sortOrder,
  filters,
  onSearch,
  onSort,
  onPageChange,
  onItemsPerPageChange,
  onColumnToggle,
  onFilterChange,
}: AuditLogGridProps) {
  const { t } = useI18n();
  const { users, loading: usersLoading } = useUsersList();

  // Build DateRange value from filters
  const dateRangeValue: DateRange = {
    startDate: filters.startDate ? filters.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
    endDate:   filters.endDate   ? filters.endDate.split('T')[0]   : new Date().toISOString().split('T')[0],
    startTime: filters.startDate && filters.startDate.includes('T') ? filters.startDate.split('T')[1].slice(0, 5) : '00:00',
    endTime:   filters.endDate   && filters.endDate.includes('T')   ? filters.endDate.split('T')[1].slice(0, 5)   : '23:59',
  };

  return (
    <div className="audit-logs-wrapper">
      {/* Filters Card - PRIMERO */}
      <div className="pt-16">
        <div className="p-3 bg-card/75 backdrop-blur-md border border-border/50 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

            {/* Row 1: Date Range | Actions */}
            <Label className="text-left">
              {t('auditLogs.filters.dateRange') || 'Date Range'}
            </Label>
            <DateRangePicker
              value={dateRangeValue}
              onChange={(range: DateRange) => onFilterChange({
                ...filters,
                startDate: `${range.startDate}T${range.startTime || '00:00'}`,
                endDate:   `${range.endDate}T${range.endTime   || '23:59'}`,
              })}
            />
            <SimpleTooltip
              content={t('auditLogs.filters.action.tooltip')}
              title={t('auditLogs.filters.action.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('auditLogs.filters.action.label')}</Label>
            </SimpleTooltip>
            <ActionMultiSelect
              selectedActions={filters.actions}
              onChange={(actions) => onFilterChange({ ...filters, actions })}
              placeholder={t('auditLogs.filters.action.all')}
            />

            {/* Row 2: User | Resource */}
            <SimpleTooltip
              content={t('auditLogs.filters.user.tooltip')}
              title={t('auditLogs.filters.user.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('auditLogs.filters.user.label')}</Label>
            </SimpleTooltip>
            <select
              value={filters.userId}
              onChange={(e) => onFilterChange({ ...filters, userId: e.target.value })}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm h-9"
              disabled={usersLoading}
            >
              <option value="">{usersLoading ? 'Loading...' : t('auditLogs.filters.user.all')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id.toString()}>
                  {user.fullName ? `${user.fullName} (${user.username})` : user.username}
                </option>
              ))}
            </select>

            <SimpleTooltip
              content={t('auditLogs.filters.resource.tooltip')}
              title={t('auditLogs.filters.resource.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('auditLogs.filters.resource.label')}</Label>
            </SimpleTooltip>
            <Input
              type="text"
              value={filters.resource}
              onChange={(e) => onFilterChange({ ...filters, resource: e.target.value })}
              placeholder={t('auditLogs.filters.resource.placeholder')}
              className="h-9"
            />
          </div>
        </div>
      </div>

      {/* Data Grid - DESPUÉS */}
      <DataGridSimple
        data={data}
        columns={columns}
        loading={loading}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={onSearch}
        onSort={onSort}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
        onColumnToggle={onColumnToggle}
        title={t('auditLogs.title')}
        titleIcon={<ScrollText className="h-5 w-5 text-primary" />}
        searchPlaceholder={t('auditLogs.searchPlaceholder')}
        emptyMessage={t('auditLogs.emptyMessage')}
      />
      
      <style dangerouslySetInnerHTML={{__html: `
        .audit-logs-wrapper {
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        
        .audit-logs-wrapper .space-y-6 > :not([hidden]) ~ :not([hidden]) {
          margin-top: 0 !important;
        }
      `}} />
    </div>
  );
}
